
import React, { useState, useEffect } from 'react';
import { sendMobileOTP, verifyMobileOTP } from '../api/verify-otp';

function MobileOTP({ data, setMobileNumberVerification }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [showModal, setShowModal] = useState(true);
  const [transactionId, setTransactionId] = useState(null);
  const [verificationKey, setVerificationKey] = useState(null);

  useEffect(() => {
    sendInitialOTP();
  }, []);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);


  const sendInitialOTP = async () => {
    if (!data?.mobileNumber) {
      setError(Liferay.Language.get("mobile-number-is-required"));
      return;
    }

    setIsSendingOTP(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await sendMobileOTP(data.mobileNumber);

      console.log("Send SMS OTP Response:", response);

      if (response && (response.statusCode === 200 || response.statusCode === '200')) {
        setSuccessMessage(
          Liferay.Language.get("otp-sent-successfully-to").replace(
            "{0}",
            formatMobileNumber(data.mobileNumber)
          )
        );

        setTransactionId(response.id || null);
        setVerificationKey(response.key || null);
        setResendTimer(60);
      } else {
        setError(
          response?.message ||
            Liferay.Language.get("failed-to-send-otp-please-try-again")
        );
        setResendTimer(0);
      }

    } catch (err) {
      console.error("Error in sendInitialOTP:", err);
      setError(Liferay.Language.get("failed-to-send-otp"));
      setResendTimer(0);
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`mobile-otp-input-${index + 1}`)?.focus();
    }

    setError('');
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`mobile-otp-input-${index - 1}`)?.focus();
    }
  };

  const verifyOtp = async () => {
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError(Liferay.Language.get("please-enter-6-digit-otp"));
      return;
    }
    if (!transactionId) {
      setError(Liferay.Language.get("transaction-id-missing-please-resend-otp"));
      return;
    }

    setIsLoading(true);
    setError('');

    try {

      const payload = {
        id: transactionId,
        mobileNumber: data.mobileNumber,
        otp: otpString,
        key: verificationKey
      };
      console.log("Verify Payload:", payload);
      const response = await verifyMobileOTP(payload);
      console.log("Verify OTP API Response:", response);

      if (response && (response.statusCode === 200 || response.statusCode === '200')) {

        if (response.otpExpired) {
          setError(Liferay.Language.get("otp-has-expired-please-resend-otp"));
          setOtp(['', '', '', '', '', '']);
          return;
        }

        if (response.matched === true) {
          setMobileNumberVerification(prev => ({
            ...prev,
            show: false,
            verified: true,
            verifiedMobileNumber: data.mobileNumber
          }));

          setSuccessMessage(
            Liferay.Language.get("mobile-number-verified-successfully")
          );
          setOtp(['', '', '', '', '', '']);
          return;
        }

        setError(
          response?.message || Liferay.Language.get("invalid-otp-please-try-again")
        );
        setOtp(['', '', '', '', '', '']);
        document.getElementById('mobile-otp-input-0')?.focus();

      } else {
        setError(
          response?.message ||
          response?.error ||
          Liferay.Language.get("verification-failed-please-try-again")
        );

        setOtp(['', '', '', '', '', '']);
        document.getElementById('mobile-otp-input-0')?.focus();
      }
    } catch (e) {
      console.error("Error in verifyOtp:", e);
      setError(Liferay.Language.get("network-error-please-try-again"));
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendTimer > 0) return;

    setIsSendingOTP(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await sendMobileOTP(data.mobileNumber);

      if (response && (response.statusCode === 200 || response.statusCode === '200')) {
        setSuccessMessage(Liferay.Language.get("otp-resent-successfully"));

        setTransactionId(response.id || null);
        setVerificationKey(response.key || null);
        setOtp(['', '', '', '', '', '']);
        setResendTimer(60);
        document.getElementById('mobile-otp-input-0')?.focus();
      } else {
        setError(response?.message || Liferay.Language.get("failed-to-resend-otp"));
      }

    } catch (err) {
      setError(Liferay.Language.get("failed-to-resend-otp"));
    } finally {
      setIsSendingOTP(false);
    }
  };

  const closePopup = () => {
    // Check if timer is running or OTP verification is in progress
    if (resendTimer > 0 || isLoading || isSendingOTP) {
      return; // Don't allow closing
    }
    setShowModal(false);
    setMobileNumberVerification(prev => ({
      ...prev,
      show: false
    }));
  };

  // Check if close buttons should be disabled
  const isCloseDisabled = resendTimer > 0 || isLoading || isSendingOTP;

  // Format mobile number for display (e.g., show last 4 digits only)
  const formatMobileNumber = (number) => {
    if (!number) return '';
    const lastFour = number.slice(-4);
    return `xxxxxx${lastFour}`;
  };

  return showModal ? (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal fade show d-block" tabIndex="-1" aria-modal="true" role="dialog">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "420px", width: "100%" }}>
          <div className="modal-content shadow-lg border-0 rounded-2 overflow-hidden">
            <div className="modal-header text-white py-2 px-3" style={{
              background: "linear-gradient(135deg,#0d6efd,#6610f2)"
            }}>
              <h5 className="modal-title fw-bold mb-0 fs-6">
                <i className="bi bi-phone me-1"></i>
                {Liferay.Language.get("mobile-number-verification")}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={closePopup}
                disabled={isCloseDisabled}
                aria-label={Liferay.Language.get("close")}
                style={{ 
                  transform: 'scale(0.8)',
                  opacity: isCloseDisabled ? '0.5' : '1',
                  cursor: isCloseDisabled ? 'not-allowed' : 'pointer'
                }}
              />
            </div>

            <div className="modal-body p-3" style={{ background: "#f8f9fc" }}>
              <div className="text-center mb-2">
                <p className="text-muted mb-1 small">
                  {Liferay.Language.get("enter-otp-sent-to-your-mobile-number")}
                </p>
              </div>

              <div className="mb-3 text-center">
                <div className="d-inline-block bg-white p-2 rounded-1 shadow-sm border">
                  <h6 className="fw-bold text-secondary text-uppercase mb-1 small" style={{ fontSize: '0.7rem' }}>
                    {Liferay.Language.get("mobile-number")}
                  </h6>
                  <p className="mb-0 fw-bold text-gray-800" style={{ fontSize: '0.9rem' }}>
                    {formatMobileNumber(data.mobileNumber)}
                  </p>
                </div>
              </div>

              {isSendingOTP && !successMessage && (
                <div className="mb-2 text-center">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">
                      {Liferay.Language.get("sending")}
                    </span>
                  </div>
                  <p className="mt-1 text-muted small">
                    {Liferay.Language.get("sending-otp")}
                  </p>
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success py-1 px-2 mb-2 text-center small" role="alert">
                  <span>{successMessage}</span>
                </div>
              )}

              {!isSendingOTP && (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-semibold mb-1 small">
                      {Liferay.Language.get("six-digit-otp")}
                    </label>
                    <div className="d-flex justify-content-center gap-1 mb-2">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`mobile-otp-input-${index}`}
                          type="text"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          className="form-control text-center"
                          style={{
                            width: '35px',
                            height: '40px',
                            fontSize: '1rem',
                            borderColor: error ? '#dc3545' : '#dee2e6',
                            padding: '0.25rem'
                          }}
                          disabled={isLoading || isSendingOTP}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>

                    {error && (
                      <div className="alert alert-danger py-1 px-2 mb-2 text-center small" role="alert">
                        <span>{error}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {!isSendingOTP && (
                <div>
                  <button
                    onClick={verifyOtp}
                    disabled={isLoading || otp.join('').length !== 6 || isSendingOTP}
                    className={`btn w-100 rounded-1 mb-2 py-1 ${isLoading || otp.join('').length !== 6 || isSendingOTP
                      ? 'btn-secondary'
                      : 'btn-primary'
                      }`}
                    style={{ fontSize: '0.9rem' }}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        {Liferay.Language.get("verifying")}
                      </>
                    ) : (
                      Liferay.Language.get("verify-otp")
                    )}
                  </button>

                  <button
                    onClick={resendOtp}
                    disabled={isLoading || isSendingOTP || resendTimer > 0}
                    className={`btn w-100 rounded-1 py-1 ${isLoading || isSendingOTP || resendTimer > 0
                      ? 'btn-outline-secondary'
                      : 'btn-outline-primary'
                      }`}
                    style={{ fontSize: '0.9rem' }}
                  >
                    {resendTimer > 0
                      ? Liferay.Language.get("resend-otp-in-x-seconds").replace(
                          "{0}",
                          String(resendTimer)
                        )
                      : Liferay.Language.get("resend-otp")}
                  </button>
                </div>
              )}
            </div>

            <div className="modal-footer border-0 p-2 bg-light">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-1 px-3 py-1"
                onClick={closePopup}
                disabled={isCloseDisabled}
                style={{ 
                  fontSize: '0.85rem',
                  opacity: isCloseDisabled ? '0.5' : '1',
                  cursor: isCloseDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                {Liferay.Language.get("close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : null;
}

export default MobileOTP;
